import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { BarChart3, User, Users, Sparkles, Check } from 'lucide-react';
export function SignupPage() {
  const navigate = useNavigate();
  const {
    signup
  } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'creator' as 'creator' | 'manager'
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setIsLoading(true);
    try {
      await signup({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role
      });
      navigate(formData.role === 'creator' ? '/creator' : '/manager');
    } catch (err) {
      setError('Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4 relative overflow-hidden" data-id="element-1450">
      {/* Premium Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1451">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" data-id="element-1452"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-500/10 rounded-full blur-3xl" data-id="element-1453"></div>
      </div>

      <Card variant="premium" className="w-full max-w-md relative z-10 animate-fade-in" data-id="element-1454">
        <CardHeader className="text-center space-y-3 pb-8" data-id="element-1455">
          <div className="mx-auto h-14 w-14 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl flex items-center justify-center text-white mb-3 shadow-lg shadow-primary-500/30" data-id="element-1456">
            <BarChart3 className="h-8 w-8" data-id="element-1457" />
          </div>
          <div data-id="element-1458">
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-2" data-id="element-1459">
              Create Account
            </CardTitle>
            <p className="text-sm text-slate-600 font-medium" data-id="element-1460">
              Join the premium influencer platform
            </p>
          </div>
        </CardHeader>

        <CardContent data-id="element-1461">
          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1462">
            <div className="space-y-2" data-id="element-1463">
              <label className="text-sm font-semibold text-slate-700" data-id="element-1464">
                I am a...
              </label>
              <div className="grid grid-cols-2 gap-4" data-id="element-1465">
                <div className={`cursor-pointer border-2 rounded-xl p-4 flex flex-col items-center space-y-2 transition-all ${formData.role === 'creator' ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/20' : 'border-slate-200 hover:border-slate-300 bg-white'}`} onClick={() => setFormData({
                ...formData,
                role: 'creator'
              })} data-id="element-1466">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${formData.role === 'creator' ? 'bg-primary-100' : 'bg-slate-100'}`} data-id="element-1467">
                    <User className={`h-6 w-6 ${formData.role === 'creator' ? 'text-primary-600' : 'text-slate-400'}`} data-id="element-1468" />
                  </div>
                  <span className={`text-sm font-semibold ${formData.role === 'creator' ? 'text-primary-900' : 'text-slate-600'}`} data-id="element-1469">
                    Creator
                  </span>
                </div>
                <div className={`cursor-pointer border-2 rounded-xl p-4 flex flex-col items-center space-y-2 transition-all ${formData.role === 'manager' ? 'border-secondary-500 bg-secondary-50 ring-2 ring-secondary-500/20' : 'border-slate-200 hover:border-slate-300 bg-white'}`} onClick={() => setFormData({
                ...formData,
                role: 'manager'
              })} data-id="element-1470">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${formData.role === 'manager' ? 'bg-secondary-100' : 'bg-slate-100'}`} data-id="element-1471">
                    <Users className={`h-6 w-6 ${formData.role === 'manager' ? 'text-secondary-600' : 'text-slate-400'}`} data-id="element-1472" />
                  </div>
                  <span className={`text-sm font-semibold ${formData.role === 'manager' ? 'text-secondary-900' : 'text-slate-600'}`} data-id="element-1473">
                    Manager
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2" data-id="element-1474">
              <label className="text-sm font-semibold text-slate-700" htmlFor="name" data-id="element-1475">
                Full Name
              </label>
              <Input id="name" placeholder="John Doe" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className="input-premium h-11" required data-id="element-1476" />
            </div>

            <div className="space-y-2" data-id="element-1477">
              <label className="text-sm font-semibold text-slate-700" htmlFor="email" data-id="element-1478">
                Email Address
              </label>
              <Input id="email" type="email" placeholder="name@company.com" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="input-premium h-11" required data-id="element-1479" />
            </div>

            <div className="space-y-2" data-id="element-1480">
              <label className="text-sm font-semibold text-slate-700" htmlFor="password" data-id="element-1481">
                Password
              </label>
              <Input id="password" type="password" placeholder="At least 6 characters" value={formData.password} onChange={e => setFormData({
              ...formData,
              password: e.target.value
            })} className="input-premium h-11" required data-id="element-1482" />
            </div>

            <div className="space-y-2" data-id="element-1483">
              <label className="text-sm font-semibold text-slate-700" htmlFor="confirmPassword" data-id="element-1484">
                Confirm Password
              </label>
              <Input id="confirmPassword" type="password" placeholder="Re-enter your password" value={formData.confirmPassword} onChange={e => setFormData({
              ...formData,
              confirmPassword: e.target.value
            })} className="input-premium h-11" required data-id="element-1485" />
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-1486">
                <p className="text-xs text-red-700 font-medium" data-id="element-1487">{error}</p>
              </div>}

            <div className="flex items-center space-x-2" data-id="element-1488">
              <input type="checkbox" id="terms" className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 focus:ring-offset-0 transition-colors" required data-id="element-1489" />
              <label htmlFor="terms" className="text-sm text-slate-600 font-medium" data-id="element-1490">
                I agree to the{' '}
                <Link to="#" className="text-primary-600 hover:text-primary-700" data-id="element-1491">
                  Terms
                </Link>{' '}
                and{' '}
                <Link to="#" className="text-primary-600 hover:text-primary-700" data-id="element-1492">
                  Privacy Policy
                </Link>
              </label>
            </div>

            <Button type="submit" variant="premium" className="w-full h-12 text-base font-semibold" disabled={isLoading} data-id="element-1493">
              {isLoading ? <span className="flex items-center" data-id="element-1494">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1495">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1496"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1497"></path>
                  </svg>
                  Creating Account...
                </span> : <span className="flex items-center" data-id="element-1498">
                  <Sparkles className="h-5 w-5 mr-2" data-id="element-1499" />
                  Create Account
                </span>}
            </Button>
          </form>

          <div className="mt-8 text-center" data-id="element-1500">
            <p className="text-sm text-slate-600" data-id="element-1501">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors" data-id="element-1502">
                Sign in
              </Link>
            </p>
          </div>

          {/* Trust Badges */}
          <div className="mt-8 pt-6 border-t border-slate-100" data-id="element-1503">
            <div className="flex items-center justify-center space-x-6 text-xs text-slate-500" data-id="element-1504">
              <div className="flex items-center space-x-1" data-id="element-1505">
                <Check className="h-4 w-4 text-green-500" data-id="element-1506" />
                <span className="font-medium" data-id="element-1507">Free Forever</span>
              </div>
              <div className="flex items-center space-x-1" data-id="element-1508">
                <Check className="h-4 w-4 text-green-500" data-id="element-1509" />
                <span className="font-medium" data-id="element-1510">No Credit Card</span>
              </div>
              <div className="flex items-center space-x-1" data-id="element-1511">
                <Check className="h-4 w-4 text-green-500" data-id="element-1512" />
                <span className="font-medium" data-id="element-1513">Cancel Anytime</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}